import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Col, OverlayTrigger, Row, Tooltip, Alert } from "react-bootstrap";
// Contexts
import { MainContext } from "../../App";
// Components
import ModalAddClassDays from "./modals/ModalAddClassDays";

// API
import { GetClassDays } from "../../api/class";

// Custom Styles
const customStyles = {
  iconContainerPlus: {
    alignContent: "center",
    color: "var(--red-primary)",
    fontSize: "17px",
    paddingRight: "4px",
  },
  iconContainerPlusDisabled: {
    alignContent: "center",
    color: "var(--red-primary)",
    fontSize: "17px",
    paddingRight: "4px",
    opacity: 0.5,
    cursor: "not-allowed",
  },
  schedulesContainer: {
    overflow: "overlay",
    maxHeight: "162px",
  },
  scheduleContainer: {
    border: "0.5px solid #B8BCBD",
    padding: "10px",
    marginBottom: "10px",
  },
};

const daysWeekMock = [
  {
    id: 1,
    name: "dayWeek.mondayShort",
    fullname: "dayWeek.monday",
    active: false,
  },
  {
    id: 2,
    name: "dayWeek.tuesdayShort",
    fullname: "dayWeek.tuesday",
    active: false,
  },
  {
    id: 3,
    name: "dayWeek.wednesdayShort",
    fullname: "dayWeek.wednesday",
    active: false,
  },
  {
    id: 4,
    name: "dayWeek.thursdayShort",
    fullname: "dayWeek.thursday",
    active: false,
  },
  {
    id: 5,
    name: "dayWeek.fridayShort",
    fullname: "dayWeek.friday",
    active: false,
  },
  {
    id: 6,
    name: "dayWeek.saturdayShort",
    fullname: "dayWeek.saturday",
    active: false,
  },
  {
    id: 7,
    name: "dayWeek.sundayShort",
    fullname: "dayWeek.sunday",
    active: false,
  },
];

const dayFields = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const lazyLoading = () => {
  return (
    <React.Fragment>
      {Array.from({ length: 4 }).map((_, index) => (
        <Row key={index} className="table-lazyLoading mt-2">
          <Col md={12}>
            <div />
          </Col>
        </Row>
      ))}
    </React.Fragment>
  );
};

const ClassDays = ({
  classId,
  setFieldSchedule,
  required,
  setClassInformationChange,
  setParentError,
}) => {
  const isNewClass = classId === "new";
  const [t] = useTranslation(["class", "global"]);
  const { permissions } = useContext(MainContext);

  const [isLoading, setIsLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [showModalAddClassDays, setShowModalAddClassDays] = useState(false);

  useEffect(() => {
    if (isNewClass) {
      setIsLoading(false);
      return;
    }
    getSchedules();
  }, []);

  /**
   * Description: Update parent component when schedules state changes
   */
  useEffect(() => {
    // Format schedules to send to parent component (formik)
    const schedulesFormatted = schedules.map((schedule) =>
      formatScheduleForParent(schedule),
    );
    setFieldSchedule(schedulesFormatted);
    // Modify state in parent component to show unsaved changes
    if (!isLoading && !isNewClass) {
      setClassInformationChange(true);
    }
  }, [schedules]);

  /**
   * Description: Function to format schedule data for parent formik
   * @param {object} schedule
   * @return {object} schedule formatted for parent
   */
  const formatScheduleForParent = (schedule) => {
    const newSchedule = {};

    dayFields.forEach((day, idx) => {
      newSchedule[day] = schedule.days[idx].active;
    });
    newSchedule.start = moment(schedule.start).unix();
    newSchedule.end = moment(schedule.end).unix();
    newSchedule.repeats = schedule.repeats;
    newSchedule.repeats_starts_on = moment(schedule.repeats_starts_on).unix();

    return newSchedule;
  };

  /**
   * Description: Function to get schedules from API
   */
  const getSchedules = () => {
    const payload = {
      class_id: classId,
    };
    setIsLoading(true);
    GetClassDays(payload)
      .then((result) => {
        if (result?.data) {
          const schedules = result.data.schedule;
          const schedulesformatted = schedules.map((schedule, index) =>
            formatSchedule(schedule, index),
          );
          setSchedules(schedulesformatted);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Description: Function to format schedule data
   * @param {object} schedule
   * @param {int} index
   * @return {object} schedule formatted
   */
  const formatSchedule = (schedule, index) => {
    const daysWeek = daysWeekMock.map((day) => ({ ...day }));
    const newSchedule = {};

    dayFields.forEach((day, idx) => {
      if (schedule[day]) daysWeek[idx].active = true;
    });

    if (schedule.start) {
      newSchedule.startView = moment.unix(schedule.start).format("hh:mm A");
      newSchedule.start = moment.unix(schedule.start);
    }
    if (schedule.end) {
      newSchedule.endView = moment.unix(schedule.end).format("hh:mm A");
      newSchedule.end = moment.unix(schedule.end);
    }
    if (schedule.repeats) {
      newSchedule.repeatsView = "repeats." + schedule.repeats;
      newSchedule.repeats = schedule.repeats;
    }
    if (schedule.repeats_starts_on) {
      newSchedule.repeats_starts_on = moment.unix(schedule.repeats_starts_on);
      newSchedule.repeatsStartsOnView = moment
        .unix(schedule.repeats_starts_on)
        .format("DD/MM/YYYY");
    }
    newSchedule.days = daysWeek;
    newSchedule.index = index;

    return newSchedule;
  };

  /**
   * Description: Function to save schedule, either adding a new one or editing an existing one
   * @param {object} schedule
   */
  const onSaveSchedule = (schedule) => {
    const isValidSchedule = validateNewSchedule(schedule);
    if (!isValidSchedule) {
      const messageError = t("class:classDays.scheduleOverlapError", {
        start: schedule.startView,
        end: schedule.endView,
      });
      setParentError(messageError);
      return;
    }
    const updatedSchedules = [...schedules];
    if (schedule.index === "new") {
      // Assign a new index based on existing indexes
      schedule.index = Math.max(-1, ...schedules.map((s) => s.index)) + 1;
      // Add new schedule
      updatedSchedules.push(schedule);
    } else {
      // Edit existing schedule
      updatedSchedules.splice(schedule.index, 1, schedule);
    }
    setSchedules(updatedSchedules);
  };

  /**
   * Description: Validate if the new schedule conflicts with any existing schedule
   * @param {object} schedule
   * @return {boolean} true if valid, false if same day active and same time range
   */
  const validateNewSchedule = (newSchedule) => {
    // Normalize a moment object to only include time (HH:mm).
    const normalizeTime = (time) => moment(time.format("HH:mm"), "HH:mm");

    // Check if two schedules have overlapping time ranges.
    const hasTimeConflict = (existingSchedule) => {
      const newStart = normalizeTime(newSchedule.start);
      const newEnd = normalizeTime(newSchedule.end);
      const existingStart = normalizeTime(existingSchedule.start);
      const existingEnd = normalizeTime(existingSchedule.end);

      return (
        newStart.isBetween(existingStart, existingEnd, null, "[)") ||
        newEnd.isBetween(existingStart, existingEnd, null, "(]") ||
        (newStart.isSameOrBefore(existingStart) &&
          newEnd.isSameOrAfter(existingEnd))
      );
    };

    // Check if two schedules have overlapping active days.
    const hasDayConflict = (existingDays) => {
      return existingDays.some(
        (day, posDay) => day.active && newSchedule.days[posDay].active,
      );
    };

    // Iterate over existing schedules to check for conflicts, ignoring the schedule being edited
    const hasConflict = schedules.some((existingSchedule) => {
      if (existingSchedule.index === newSchedule.index) return false; // Skip the current schedule being edited

      return (
        hasDayConflict(existingSchedule.days) &&
        hasTimeConflict(existingSchedule)
      );
    });

    return !hasConflict;
  };

  /**
   * Description: Function to delete a schedule
   * @param {int} index
   */
  const onDeleteSchedule = (index) => {
    const updatedSchedules = [...schedules];
    // Remove schedule at the given index
    updatedSchedules.splice(index, 1);
    // Update state
    setSchedules(updatedSchedules);
  };

  return isLoading ? (
    lazyLoading()
  ) : (
    <React.Fragment>
      <Row id="classDaysContainer" className="mb-2">
        <Col xs={11} className="fw-bold fs-5" id="classDaysTitle">
          {t("classDays.title")}
        </Col>
        <Col
          xs={1}
          id="addClassDaysContainer"
          className={!permissions.classes.create ? "icon-btn-disabled" : ""}
          style={
            !permissions.classes.create
              ? customStyles.iconContainerPlusDisabled
              : customStyles.iconContainerPlus
          }
        >
          <OverlayTrigger
            placement="top"
            overlay={
              !permissions.classes.create ? (
                <span />
              ) : (
                <Tooltip id="addClassDaysTooltip">
                  {t("global:buttons.add")}
                </Tooltip>
              )
            }
          >
            <i
              id="addClassDaysIcon"
              role="button"
              className="bi bi-plus-circle-fill float-end"
              style={{
                cursor: permissions.classes.create ? "pointer" : "not-allowed",
              }}
              onClick={
                permissions.classes.create
                  ? () => setShowModalAddClassDays(true)
                  : null
              }
            />
          </OverlayTrigger>
        </Col>
      </Row>
      <Row id="schedulesContainer" className="mb-3">
        <Col id="schedulesCol" style={customStyles.schedulesContainer}>
          {schedules.map((schedule, index) => {
            return (
              <div
                id={`scheduleContainer-${index}`}
                md={12}
                key={schedule.index}
                onClick={
                  permissions.classes.create
                    ? () => {
                        setSchedule(schedule);
                        setShowModalAddClassDays(true);
                      }
                    : null
                }
                style={{
                  ...customStyles.scheduleContainer,
                  cursor: permissions.classes.create
                    ? "pointer"
                    : "not-allowed",
                }}
              >
                {schedule.days?.map((day, i) => {
                  return (
                    <Row key={i} className={!day.active ? "hide" : ""}>
                      <Col md={4}>{t(`global:${day.fullname}`)}</Col>
                      <Col md={4}>
                        {schedule.startView + " - " + schedule.endView}
                      </Col>
                      <Col md={4}>{t(schedule.repeatsView)}</Col>
                    </Row>
                  );
                })}
              </div>
            );
          })}
        </Col>
      </Row>
      {required && (
        <Row>
          <Col md={12} className="mt-3">
            <Alert variant="warning">{t("classDays.requiredClassDay")}</Alert>
          </Col>
        </Row>
      )}
      {/** Modals */}
      {showModalAddClassDays && (
        <ModalAddClassDays
          show={showModalAddClassDays}
          onHide={() => {
            setShowModalAddClassDays(false);
            setSchedule(null);
          }}
          schedule={schedule}
          onSave={onSaveSchedule}
          onDelete={onDeleteSchedule}
        />
      )}
    </React.Fragment>
  );
};

ClassDays.propTypes = {
  classId: PropTypes.string.isRequired,
  setFieldSchedule: PropTypes.func,
  required: PropTypes.bool,
  setClassInformationChange: PropTypes.func,
  setParentError: PropTypes.func,
};

ClassDays.defaultProps = {
  setFieldSchedule: () => {},
  required: false,
  setClassInformationChange: () => {},
  setParentError: () => {},
};

export default ClassDays;
